import Chip from "@material-ui/core/Chip"
import { makeStyles, Tooltip } from "@material-ui/core"
import { AlertCircle } from "react-feather"
import { useTranslation } from "react-i18next"

type Translations = typeof defaultTranslations

type Membership = {
  fullname: string
  role: string
  isOwner: boolean
  isStandardMember: boolean
  isLimitedMember: boolean
  hasAcceptedInvitation: boolean
}

type RoleChipProps = {
  membership: Membership
}

type MembershipType = {
  label: string
  isNoAccess: boolean
}

export const RoleChip = ({ membership }: RoleChipProps) => {
  const translations = useTranslations({ member: membership.fullname })
  const chipProps = getChipProps(membership, translations)
  const classes = useStyles()
  const roleDescription = getRoleDescription(membership, translations)

  return (
    <Tooltip title={roleDescription} arrow>
      <Chip avatar={<AlertCircle />} size="small" className={classes.roleChip} {...chipProps} />
    </Tooltip>
  )
}

const useStyles = makeStyles((theme) => ({
  roleChip: {
    borderRadius: 6,
    width: "fit-content",
    fontWeight: 700,
    fontSize: 12,

    "&.MuiChip-colorPrimary": {
      backgroundColor: theme.palette.info.main,
    },

    "&.MuiChip-colorSecondary": {
      backgroundColor: theme.palette.success.dark,
    },

    "& .MuiChip-avatar": {
      backgroundColor: "transparent",
      height: 14,
      width: 14,
      marginLeft: 8,
    },
  },
}))

const getMembershipType = (membership: Membership, translations: Translations): MembershipType => {
  if (!membership.hasAcceptedInvitation) {
    return { label: translations.noAccessMemberLabel, isNoAccess: true }
  }

  const label = membership.isOwner
    ? translations.orgOwnerLabel
    : membership.isStandardMember
    ? translations.standardMemberLabel
    : translations.limitedMemberLabel
  return { label, isNoAccess: false }
}

const getChipProps = (membership: Membership, translations: Translations) => {
  const membershipType = getMembershipType(membership, translations)

  if (membershipType.isNoAccess) {
    return { label: membershipType.label, variant: "outlined", color: "default" } as const
  }

  const color = membership.isOwner ? "primary" : membership.isStandardMember ? "secondary" : "default"
  return { label: membershipType.label, color } as const
}

const getRoleDescription = (membership: Membership, translations: Translations): string => {
  const membershipType = getMembershipType(membership, translations)
  if (membershipType.isNoAccess) {
    return translations.noAccessMemberInfo
  }

  if (membership.isLimitedMember) {
    return translations.limitedMembershipHelperText
  }

  if (membership.isOwner) {
    return translations.ownerAccessInfo
  }

  return translations.normalMembershipHelperText
}

export const useTranslations = ({ member = "" }: { member?: string } = {}): Translations => {
  const { t: tMembership } = useTranslation("membership")
  return {
    orgOwnerLabel: tMembership("membershipSettingsView.orgOwnerLabel", {
      defaultValue: defaultTranslations.orgOwnerLabel,
    }),
    standardMemberLabel: tMembership("standardMemberLabel", {
      defaultValue: defaultTranslations.standardMemberLabel,
    }),
    limitedMemberLabel: tMembership("limitedMemberLabel", {
      defaultValue: defaultTranslations.limitedMemberLabel,
    }),
    noAccessMemberLabel: tMembership("membershipSettingsView.noAccessMemberLabel", {
      defaultValue: defaultTranslations.noAccessMemberLabel,
    }),
    normalMembershipHelperText: tMembership("newMemberPage.normalMembershipHelperText", {
      defaultValue: defaultTranslations.normalMembershipHelperText,
    }),
    limitedMembershipHelperText: tMembership("newMemberPage.limitedMembershipHelperText", {
      defaultValue: defaultTranslations.limitedMembershipHelperText,
    }),
    ownerAccessInfo: tMembership("ownerAccessInfo", {
      defaultValue: defaultTranslations.ownerAccessInfo,
      member,
    }),
    noAccessMemberInfo: tMembership("noAccessMemberInfo", {
      defaultValue: defaultTranslations.noAccessMemberInfo,
      member,
    }),
  }
}

const defaultTranslations = {
  orgOwnerLabel: "Organization owner",
  limitedMemberLabel: "Limited member",
  standardMemberLabel: "Standard member",
  noAccessMemberLabel: "No access",
  normalMembershipHelperText:
    "A standart member has access to all projects, tasks, to-dos, resources, and other information within the organisation. However, specific actions can be restricted by adjusting the member's permissions.",
  limitedMembershipHelperText:
    "A limited member can access their own projects, tasks, to-dos, and other relevant information within the organisation. Additionally, they will be able to view resources specified in the input field of this form. Permissions for a limited member operate in the same way as those for a regular member.",
  ownerAccessInfo:
    "As an owner, {{member}} has full access to perform all actions within the organization. Owner's permissions cannot be modified.",
  noAccessMemberInfo:
    "{{member}} hasn't joined this organization yet. Invite them by sending an invitation to their email address.",
}
